import {useEffect} from 'react';
import {useForm} from 'react-hook-form';

import {ProductVariant, Subscription} from '#apollo/schema';
import {usePauseSubscriptionItemsMutation, usePauseSubscriptionQuery} from '#apollo/schema';
import {Button, Icon, Modal, Text, UncertainProgress} from '#component/base';
import {ModalClose} from '#component/base/Modal';
import {supplements} from '#constant/products';
import {useToastQueue} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {
  getProductIdsByProductTids,
  getProductIdsByVariantIds,
  getSubscriptionVariantIdsByProductIds,
  getVariantIdsByProductIds,
  hasSubscriptionItem,
} from '#util/subscriptions';

import {SkipNextOrderModalItem} from './SkipNextOrderModalItem';

import {FormData, SkipNextOrderModalContentProps} from './SkipNextOrderModal.types';

import {button, container, description} from './SkipNextOrderModal.css';

export const SkipNextOrderModalContent = ({
  productVariants,
  setOpen,
}: SkipNextOrderModalContentProps) => {
  const {addToast} = useToastQueue();
  const {data, loading} = usePauseSubscriptionQuery();
  const [pauseSubscriptionItems, {loading: submitting}] = usePauseSubscriptionItemsMutation({
    refetchQueries: [
      'PauseSubscriptionQuery',
      'ProfileUpcomingOrderQuery',
      'ProfileCustomerSubscriptionQuery',
      'ManagePlanQuery',
    ],
  });

  // all handling of items in the skip flow is done in products
  // the products are converted to variants right before the pauseSubcriptionItems
  // mutation is called
  const {watch, handleSubmit, reset, setValue, getValues} = useForm<FormData>({
    defaultValues: {
      productsToPauseOnNextOrder: [],
      productsToUnpauseOnNextOrder: [],
    },
  });

  const subscription = data?.me?.customerAccount?.subscription;

  useEffect(() => {
    if (subscription) {
      const pausedItems = subscription.pausedProductVariantIds || [];
      const pausedProducts = getProductIdsByVariantIds(productVariants, pausedItems);
      reset({
        productsToPauseOnNextOrder: pausedProducts,
        productsToUnpauseOnNextOrder: [],
      });
    }
  }, [subscription, reset]);

  if (!subscription) return null;

  const watchProductsToPause = watch('productsToPauseOnNextOrder');
  const watchProductsToUnpause = watch('productsToUnpauseOnNextOrder');

  const isSelected = (productId: string) => {
    return watchProductsToPause?.includes(productId) && !watchProductsToUnpause.includes(productId);
  };

  const setSelected = (productId: string, selected: boolean, hasSupplementPaused?: boolean) => {
    if (hasSupplementPaused) {
      const currentUnpauseProducts = getValues('productsToUnpauseOnNextOrder');
      const updatedUnpauseProducts = !selected
        ? [...currentUnpauseProducts, productId]
        : currentUnpauseProducts.filter(id => id !== productId);
      setValue('productsToUnpauseOnNextOrder', updatedUnpauseProducts);
    } else {
      const currentPauseProducts = getValues('productsToPauseOnNextOrder');
      const updatedPauseProducts = selected
        ? [...currentPauseProducts, productId]
        : currentPauseProducts.filter(id => id !== productId);
      setValue('productsToPauseOnNextOrder', updatedPauseProducts);
    }
  };

  const checkSupplement = (
    subscription: Subscription,
    variants: ProductVariant[],
    productTid: string,
  ) => {
    const supplementProductId = getProductIdsByProductTids(variants, [productTid]);
    const hasSupplement = hasSubscriptionItem(subscription.items, supplementProductId);
    const supplementVariants: ProductVariant[] = variants.filter(
      v => v.productId.toString() === supplementProductId[0],
    );
    const supplementVariantIds: string[] = supplementVariants.map(
      supplementVariant => supplementVariant.id,
    );
    const hasSupplementPaused = subscription.pausedProductVariantIds.some(id =>
      supplementVariantIds.includes(id),
    );
    return {hasSupplement, hasSupplementPaused};
  };

  const renderSupplementItems = () => {
    return supplements.map(supplement => {
      const {hasSupplement, hasSupplementPaused} = checkSupplement(
        subscription as Subscription,
        productVariants,
        supplement.productTid,
      );

      const unpausedVariantId = subscription.items
        .map(
          item =>
            productVariants.find(
              pv => pv.id === item.variantId && pv.product?.tid === supplement.productTid,
            )?.id,
        )
        .find(Boolean);

      const pausedVariantId = subscription.pausedProductVariantIds
        .map(
          item =>
            productVariants.find(pv => pv.id === item && pv?.product?.tid === supplement.productTid)
              ?.id,
        )
        .find(Boolean);

      const variantId = unpausedVariantId || pausedVariantId;

      if (!variantId) return null;

      const productId: string = getProductIdsByVariantIds(productVariants, [variantId])[0];

      return (
        (hasSupplement || hasSupplementPaused) && (
          <SkipNextOrderModalItem
            key={productId}
            productId={productId}
            title={supplement.title}
            image={supplement.skipNextOrderImage}
            selected={isSelected(productId)}
            setSelected={setSelected}
            hasSupplementPaused={hasSupplementPaused}
          />
        )
      );
    });
  };

  const handleUpdate = async (data: FormData) => {
    try {
      const pauseProductVariantIdsInSubscription: string[] = getSubscriptionVariantIdsByProductIds(
        subscription.items,
        data.productsToPauseOnNextOrder,
      );
      const unpauseProductVariantIdsInSubscription: string[] = getVariantIdsByProductIds(
        productVariants,
        data.productsToUnpauseOnNextOrder,
      );

      const input = {
        subscriptionId: Number(subscription.id),
        pauseProductVariantIds: pauseProductVariantIdsInSubscription.filter(
          id => !subscription.pausedProductVariantIds.includes(id),
        ),
        unpauseProductVariantIds: unpauseProductVariantIdsInSubscription.filter(id =>
          subscription.pausedProductVariantIds.includes(id),
        ),
      };

      await pauseSubscriptionItems({variables: {input}});
      setOpen?.(false);
      addToast({
        children: 'Your subscription has been updated!',
      });
    } catch (error) {
      addToast({
        children: 'Uh oh, something went wrong!',
      });
      throw error;
    }
  };

  const hasChanges = () => {
    const variantsToPause = getSubscriptionVariantIdsByProductIds(
      subscription.items,
      watchProductsToPause,
    );
    const variantsToUnpause = getSubscriptionVariantIdsByProductIds(
      subscription.items,
      watchProductsToUnpause,
    );

    return (
      variantsToPause.length !== subscription.pausedProductVariantIds.length ||
      variantsToUnpause.length > 0 ||
      variantsToPause.some(id => !subscription.pausedProductVariantIds.includes(id)) ||
      variantsToUnpause.some(id => subscription.pausedProductVariantIds.includes(id))
    );
  };

  return (
    <Modal layout="drawer" variant="light" hideCloseButton={false}>
      <div className={container}>
        <div>
          <Text
            as="h3"
            variant="heading3"
            color="burgundy"
            className={sprinkles({paddingBottom: 10})}>
            {submitting ? 'Updating skipped items' : 'Skip your next order'}
          </Text>
          {!loading && !submitting && (
            <Text as="p" className={description} color="doveGray">
              Check items you want skipped from being sent in your upcoming order. You may uncheck
              items that you no longer want skipped.
            </Text>
          )}
        </div>
        <ModalClose className={button}>
          <Icon aria-label="Exit Check-in" color="burgundy" name="X" />
        </ModalClose>
      </div>
      {loading || submitting ? (
        <div className={sprinkles({width: 'full', flex: 'flex', content: 'center', pY: 40})}>
          <UncertainProgress width={144} size={24} duration={1} />
        </div>
      ) : (
        <>
          {renderSupplementItems()}
          <Button
            variant="primary"
            loading={submitting}
            onClick={handleSubmit(handleUpdate)}
            disabled={!hasChanges() || submitting}
            className={sprinkles({
              marginBottom: 20,
              marginTop: 20,
            })}>
            Update
          </Button>
          <Text variant="caption" style={{fontSize: 12}} color="doveGray">
            Any updates to your order will be reflected in your next monthly payment
          </Text>
        </>
      )}
    </Modal>
  );
};
